import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import lodashDebounce from 'lodash/debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { defaultColorPalette } from '../palette';
import { Saturation, Hue, Alpha, CheckBoard } from './common';
import * as colorHelper from './helpers/color';
import SketchFields from './sketch/SketchFields';
import SketchPresetColors from './sketch/SketchPresetColors';
const defaultColor = {
    h: 250,
    s: 0.5,
    l: 0.2,
    a: 1,
};
const styles = {
    picker: (width) => ({
        width: typeof width === 'string' ? width : `${width}px`,
        padding: '10px 10px 0',
        boxSizing: 'initial',
        background: '#fff',
        borderRadius: '4px',
        boxShadow: '0 0 0 1px rgba(0,0,0,.15), 0 8px 16px rgba(0,0,0,.15)',
    }),
    controls: {
        display: 'flex',
    },
    sliders: {
        padding: '4px 0',
        flex: '1',
    },
    color: (disableAlpha) => ({
        width: '24px',
        height: disableAlpha ? '10px' : '24px',
        position: 'relative',
        marginTop: '4px',
        marginLeft: '4px',
        borderRadius: '3px',
    }),
    activeColor: (rgb) => ({
        position: 'absolute',
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        borderRadius: '2px',
        background: `rgba(${rgb.r},${rgb.g},${rgb.b},${rgb.a})`,
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.15), inset 0 0 4px rgba(0,0,0,.25)',
    }),
    saturationContainer: {
        width: '100%',
        paddingBottom: '75%',
        position: 'relative',
        overflow: 'hidden',
    },
    hueContainer: {
        position: 'relative',
        height: '10px',
        overflow: 'hidden',
    },
    hueElement: {
        borderRadius: '2px',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.15), inset 0 0 4px rgba(0,0,0,.25)',
    },
    alphaContainer: (disableAlpha) => ({
        position: 'relative',
        height: '10px',
        marginTop: '4px',
        overflow: 'hidden',
        ...(disableAlpha && { display: 'none' }),
    }),
    alphaElement: {
        borderRadius: '2px',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.15), inset 0 0 4px rgba(0,0,0,.25)',
    },
};
export function ColorPicker(props) {
    const { width = 200, onChange, onSwatchHover, disableAlpha = false, presetColors = defaultColorPalette, className = '', color = defaultColor, onChangeComplete, style = {}, } = props;
    const [state, setState] = useState(colorHelper.toState(color, 0));
    const debounce = useRef(lodashDebounce((fn, data, event) => {
        fn(data, event);
    }, 100)).current;
    useEffect(() => {
        setState((prevState) => colorHelper.toState(color, prevState.oldHue));
    }, [color]);
    const handleChange = useCallback((data, event) => {
        const isValidColor = colorHelper.simpleCheckForValidColor(data);
        if (isValidColor) {
            const colors = colorHelper.toState(data, data.h || state.oldHue);
            setState(colors);
            if (onChangeComplete) {
                debounce(onChangeComplete, colors, event);
            }
            if (onChange) {
                onChange(colors, event);
            }
        }
    }, [debounce, onChange, onChangeComplete, state.oldHue]);
    const handleSwatchHover = useCallback((data, event) => {
        const isValidColor = colorHelper.simpleCheckForValidColor(data);
        if (isValidColor) {
            const colors = colorHelper.toState(data, data.h || state.oldHue);
            if (onSwatchHover) {
                onSwatchHover(colors, event);
            }
        }
    }, [onSwatchHover, state.oldHue]);
    const { rgb, hex, hsv, hsl } = state;
    return (_jsxs("div", { style: { ...styles.picker(width), ...style }, className: className, children: [_jsx("div", { style: styles.saturationContainer, children: _jsx(Saturation, { hsl: hsl, hsv: hsv, onChange: handleChange }) }), _jsxs("div", { style: styles.controls, children: [_jsxs("div", { style: styles.sliders, children: [_jsx("div", { style: styles.hueContainer, children: _jsx(Hue, { style: styles.hueElement, hsl: hsl, onChange: handleChange }) }), _jsx("div", { style: styles.alphaContainer(disableAlpha), children: _jsx(Alpha, { style: styles.alphaElement, rgb: rgb, hsl: hsl, onChange: handleChange }) })] }), _jsxs("div", { style: styles.color(disableAlpha), children: [_jsx(CheckBoard, {}), _jsx("div", { style: styles.activeColor(rgb) })] })] }), _jsx(SketchFields, { hsl: hsl, hex: hex, rgb: rgb, onChange: handleChange, disableAlpha: disableAlpha }), _jsx(SketchPresetColors, { colors: presetColors, onClick: (data, e) => handleChange?.(data, e), onSwatchHover: handleSwatchHover })] }));
}
//# sourceMappingURL=ColorPicker.js.map